import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../styles/DraftExecutionStatus.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const POLL_INTERVAL = 30000; // 30 seconds
const getFileName = (path) => path?.split("\\").pop();

const DraftExecutionStatus = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [batchInfo, setBatchInfo] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  
  /* ================= FETCH JOB ROW STATUS ================= */
  const fetchJobStatus = useCallback(async () => {
    try {
      setError("");
      const res = await fetch(
        `${API_BASE}/drafts/job-status/${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );

      if (!res.ok) {
        throw new Error("Failed to fetch job status");
      }

      const data = await res.json();
	  setRows(Array.isArray(data) ? data : data.rows || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("JOB STATUS ERROR:", err);
      setError("Unable to load job execution status");
    } finally {
      setLoading(false);
    }
  }, [jobId]);
  
  /* ================= FETCH JOB ROW STATUS ================= */
  const fetchBatchJob = useCallback(async () => {
	  try {
		setError("");

		const res = await fetch(
		  `${API_BASE}/drafts/${jobId}`,
		  {
			headers: {
			  Authorization: `Bearer ${localStorage.getItem("token")}`
			}
		  }
		);

		if (!res.ok) {
		  throw new Error("Failed to fetch batch info");
		}

		const data = await res.json();

		setBatchInfo(data[0]);   // ✅ STORE IN STATE
	  } catch (err) {
		console.error("BATCH INFO ERROR:", err);
		setError("Unable to load batch information");
	  }
	}, [jobId]);

  /* ================= POLLING ================= */
  useEffect(() => {
    fetchJobStatus(); // initial load
	fetchBatchJob(); 
	
    const interval = setInterval(fetchJobStatus, POLL_INTERVAL);

    return () => clearInterval(interval); // cleanup
  }, [fetchJobStatus, fetchBatchJob]);

  /* ================= HELPERS ================= */
  const getStatusClass = (status) => {
    switch (status) {
      case "COMPLETED":
        return "status success";
      case "FAILED":
        return "status failed";
      case "IN_PROGRESS":
        return "status progress";
      default:
        return "status pending";
    }
  };
  
  /* ================= FILE SELECTION ================= */

	const handleSelectOne = (filePath) => {
	  setSelectedFiles((prev) =>
		prev.includes(filePath)
		  ? prev.filter((f) => f !== filePath)
		  : [...prev, filePath]
	  );
	};

	const handleSelectAll = () => {
	  if (selectAll) {
		setSelectedFiles([]);
	  } else {
		const allFiles = rows
		  .filter((r) => r.output_file)
		  .map((r) => r.output_file);

		setSelectedFiles(allFiles);
	  }

	  setSelectAll(!selectAll);
	};


/*   const downloadFile = (filePath) => {
    window.open(
      `${API_BASE}/drafts/download?path=${encodeURIComponent(filePath)}`,
      "_blank"
    );
  }; */
  
	const downloadFile = async (filePath) => {
	  try {
		const token = localStorage.getItem("token");

		const res = await fetch(
		  `${API_BASE}/drafts/download?path=${encodeURIComponent(filePath)}`,
		  {
			headers: {
			  Authorization: `Bearer ${token}`,
			},
		  }
		);

		if (!res.ok) {
		  throw new Error("Download failed");
		}

		const blob = await res.blob();

		const url = window.URL.createObjectURL(blob);

		const a = document.createElement("a");
		a.href = url;
		a.download = getFileName(filePath); // filename
		document.body.appendChild(a);

		a.click();

		a.remove();
		window.URL.revokeObjectURL(url);

	  } catch (err) {
		console.error("DOWNLOAD ERROR:", err);
		alert("Unable to download file");
	  }
	};

	/* ================= MULTI DOWNLOAD ================= */

	const downloadMultiple = async (files) => {
	  for (const file of files) {
		await downloadFile(file);
	  }
	};

	const downloadSelected = () => {
	  if (selectedFiles.length === 0) {
		alert("Please select at least one file");
		return;
	  }

	  downloadMultiple(selectedFiles);
	};

	const downloadAll = () => {
	  const allFiles = rows
		.filter((r) => r.output_file)
		.map((r) => r.output_file);

	  if (allFiles.length === 0) {
		alert("No files available to download");
		return;
	  }

	  downloadMultiple(allFiles);
	};



  /* ================= RENDER ================= */

  return (
    <div className="execution-container">
      <h2>Draft Execution Status</h2>

      <p className="job-id">
        <strong>Job Batch ID:</strong> {jobId}
      </p>

      {loading && <p>Loading execution status...</p>}
      {error && <p className="error">{error}</p>}

      {!loading && rows.length === 0 && (
        <p>No rows found for this job.</p>
      )}

      {/* ===== JOB BATCH INFO ===== */}
      {batchInfo && (
		  <div className="draft-info">

			<div>
			  <span>User ID</span>
			  {batchInfo.user_name}
			</div>
			<div>
			  <span>Input File</span>
			  {batchInfo.input_file?.split("\\").pop()}
			</div>
			<div>
			  <span>Range</span>
			  {batchInfo.start_number}-{batchInfo.end_number}
			</div>
			<div>
			  <span>Template File</span>
			  {batchInfo.template_file?.split("\\").pop()}
			</div>
			<div>
			  <span>Draft Type</span>
			  {batchInfo.draft_type}
			</div>
			<div>
			  <span>Document Sections</span>
			  {batchInfo.document_section === "Whole Document"
				? "Whole Document"
				: JSON.parse(batchInfo.document_section || "[]")
					.map((s) => s.name)
					.join(", ")}
			</div>
			<div>
			  <span>Created At</span>
			  {batchInfo.createdAt
				? new Date(batchInfo.createdAt).toLocaleString()
				: "-"}
			</div>
			<div>
			  <span>Status</span>

			  <span
				style={{
				  color:
					batchInfo.batch_status === "COMPLETED"
					  ? "green"
					  : batchInfo.batch_status === "FAILED"
					  ? "red"
					  : "black",
				  fontWeight: 600,
				  
				}}
			  >
				{batchInfo.batch_status}
			  </span>
			</div>

		  </div>
		)}

		      {/* ===== ACTION ===== */}
      <div className="execution-actions">

		  <button
			className="secondary-btn"
			onClick={downloadSelected}
		  >
			Download Selected
		  </button>

		  <button
			className="secondary-btn"
			onClick={downloadAll}
		  >
			Download All
		  </button>

		  <button
			className="secondary-btn"
			onClick={() =>
			  navigate("/admin", { state: { fromExecution: true } })
			}
		  >
			Back to Drafts
		  </button>

		</div>
		
      {/* ===== JOB CARD TABLE ===== */}
      <table className="status-table">
        <thead>
		  <tr>
			<th>
			  <input
				type="checkbox"
				checked={selectAll}
				onChange={handleSelectAll}
			  />
			</th>
			<th>Job Card ID</th>
			<th>Input File Row No</th>
			<th>Identifier</th>
			<th>Title</th>
			<th>Status</th>
			<th>Fail Reason</th>
			<th>CreatedAt</th>
			<th>Output File</th>
		  </tr>
		</thead>

        <tbody>
          {rows.map((row) => (
            <tr key={row.job_card_id}>
              {/* Checkbox */}
			  <td>
				{row.output_file && (
				  <input
					type="checkbox"
					checked={selectedFiles.includes(row.output_file)}
					onChange={() => handleSelectOne(row.output_file)}
				  />
				)}
			  </td>
			  <td>{row.job_card_id}</td>
              <td>{row.input_file_row}</td>
              <td>{row.identifier}</td>
              <td>{row.title}</td>
              <td
                style={{
                  color:
                    row.job_status === "COMPLETED"
                        ? "blue"
                        : row.job_status === "SUBMITTED"
                        ? "green"
						: row.job_status === "FAILED"
                        ? "red"
                        : "black",
                  fontWeight: 600,
                }}
              >
                {row.job_status}
              </td>
              <td>{row.failed_reason || "-"}</td>
              <td>{row.createdAt || "-"}</td>
              <td>
				{row.output_file ? (
					<button
					  className="link-btn"
					  onClick={() => downloadFile(row.output_file)}
					>
					  {getFileName(row.output_file)}
					</button>
				  ) : (
					"-"
				  )}
			  </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ===== ACTION ===== */}
      <div className="execution-actions">

		  <button
			className="secondary-btn"
			onClick={downloadSelected}
		  >
			Download Selected
		  </button>

		  <button
			className="secondary-btn"
			onClick={downloadAll}
		  >
			Download All
		  </button>

		  <button
			className="secondary-btn"
			onClick={() =>
			  navigate("/admin", { state: { fromExecution: true } })
			}
		  >
			Back to Drafts
		  </button>

		</div>

    </div>
  );
};

export default DraftExecutionStatus;
